import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = "https://api.vyapakprint.com/api/banner";

const PANEL_OPTIONS = [
  { label: "Client Panel", value: "client" },
  { label: "Business Panel", value: "business" },
];

const AddBanner = () => {
  const [panelType, setPanelType] = useState("client");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [banners, setBanners] = useState([]);

  const [loading, setLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const [notice, setNotice] = useState({ type: "", message: "" });

  const showNotice = (type, message) => setNotice({ type, message });

  const getErrorMessage = (err, fallback = "Something went wrong.") => {
    return (
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      fallback
    );
  };

  const sortedBanners = useMemo(() => {
    return [...banners].sort((a, b) => (a?.index ?? 0) - (b?.index ?? 0));
  }, [banners]);

  const remainingSlots = Math.max(0, 10 - (banners?.length || 0));

  useEffect(() => {
    fetchBanners(panelType);
    setSelectedFiles([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelType]);

  const fetchBanners = async (type = panelType) => {
    setLoading(true);
    setNotice({ type: "", message: "" });

    try {
      const endpoint =
        type === "business" ? "getBusinessBanners" : "getClientBanners";

      const res = await axios.get(`${API}/${endpoint}`);
      setBanners(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      showNotice("error", getErrorMessage(err, "Error fetching banners."));
      setBanners([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePanelChange = (type) => {
    setPanelType(type);
    setSelectedFiles([]);
    setNotice({ type: "", message: "" });
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    e.target.value = "";

    const onlyImages = files.filter((f) => f.type?.startsWith("image/"));
    if (onlyImages.length !== files.length) {
      showNotice("error", "Only image files are allowed.");
    }

    const existingKey = new Set(
      selectedFiles.map((f) => `${f.name}-${f.size}-${f.lastModified}`),
    );

    const unique = onlyImages.filter(
      (f) => !existingKey.has(`${f.name}-${f.size}-${f.lastModified}`),
    );

    if (remainingSlots <= 0) {
      showNotice(
        "error",
        `You already have 10 ${panelType} banners. Delete one to upload new.`,
      );
      return;
    }

    const allowed = unique.slice(0, remainingSlots - selectedFiles.length);

    if (allowed.length === 0) {
      showNotice(
        "info",
        `You can upload only ${
          remainingSlots - selectedFiles.length
        } more banner(s) in ${panelType} panel.`,
      );
      return;
    }

    setSelectedFiles((prev) => [...prev, ...allowed]);

    if (unique.length > allowed.length) {
      showNotice(
        "info",
        `Only ${allowed.length} file(s) added due to 10 banner limit for ${panelType} panel.`,
      );
    } else {
      showNotice(
        "success",
        `${allowed.length} file(s) added for ${panelType} panel. Click “Save Banners” to upload.`,
      );
    }
  };

  const removeSelectedFile = (idx) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const clearSelected = () => setSelectedFiles([]);

  const handleSubmit = async () => {
    if (selectedFiles.length === 0) {
      showNotice("error", "Please select at least one image.");
      return;
    }

    if (selectedFiles.length > remainingSlots) {
      showNotice(
        "error",
        `Only ${remainingSlots} slot(s) remaining in ${panelType} panel. Remove extra files.`,
      );
      return;
    }

    const formData = new FormData();
    selectedFiles.forEach((file) => formData.append("images", file));

    setIsUploading(true);
    setNotice({ type: "", message: "" });

    try {
      const res = await axios.post(`${API}/upload/${panelType}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const successMsg =
        res?.data?.message ||
        (Array.isArray(res.data)
          ? "Images uploaded successfully."
          : "Upload successful.");

      showNotice("success", successMsg);
      clearSelected();
      await fetchBanners(panelType);
    } catch (err) {
      showNotice("error", getErrorMessage(err, "Error uploading images."));
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (id) => {
    const ok = window.confirm("Are you sure you want to delete this banner?");
    if (!ok) return;

    setDeletingId(id);
    setNotice({ type: "", message: "" });

    try {
      const res = await axios.delete(`${API}/deleteBanner/${id}`);
      const msg = res?.data?.message || "Banner deleted successfully.";
      setBanners((prev) => prev.filter((b) => b._id !== id));
      showNotice("success", msg);
    } catch (err) {
      showNotice("error", getErrorMessage(err, "Error deleting banner."));
    } finally {
      setDeletingId(null);
    }
  };

  const NoticeBox = () => {
    if (!notice.message) return null;

    const base = "px-4 py-3 rounded-lg text-sm border";
    const styles =
      notice.type === "success"
        ? "bg-green-50 border-green-200 text-green-800"
        : notice.type === "error"
          ? "bg-red-50 border-red-200 text-red-800"
          : "bg-blue-50 border-blue-200 text-blue-800";

    return <div className={`${base} ${styles}`}>{notice.message}</div>;
  };

  return (
    <div className="container mx-auto p-4 md:p-6 space-y-8">
      <h2 className="text-2xl md:text-3xl font-semibold text-gray-800 text-center">
        Manage Banners
      </h2>

      <NoticeBox />

      {/* Panel Selector */}
      <div className="bg-white rounded-lg shadow-lg p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="text-lg md:text-xl font-semibold text-gray-700">
              Select Panel
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Manage banners separately for client and business panels.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            {PANEL_OPTIONS.map((item) => {
              const active = panelType === item.value;
              return (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => handlePanelChange(item.value)}
                  className={`px-4 py-2 rounded-lg border text-sm font-medium transition ${
                    active
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Upload Section */}
      <div className="bg-white rounded-lg shadow-lg p-4 md:p-6 space-y-4">
        <div>
          <h3 className="text-xl font-semibold text-gray-700">
            Add Banner Images
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Selected Panel:{" "}
            <span className="font-semibold capitalize">{panelType}</span>
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Limit: 10 banners per panel. Remaining slots:{" "}
            <span className="font-semibold">{remainingSlots}</span>
          </p>
        </div>

        <input
          type="file"
          multiple
          accept="image/*"
          onChange={handleImageSelect}
          disabled={remainingSlots <= 0}
          className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer p-2"
        />

        {selectedFiles.length > 0 && (
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="font-semibold text-gray-700">
                Selected ({selectedFiles.length})
              </p>
              <button
                onClick={clearSelected}
                className="text-sm text-gray-600 hover:text-gray-900"
                disabled={isUploading}
              >
                Clear
              </button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {selectedFiles.map((file, idx) => {
                const previewUrl = URL.createObjectURL(file);
                return (
                  <div
                    key={`${file.name}-${idx}`}
                    className="relative border rounded-md overflow-hidden"
                  >
                    <img
                      src={previewUrl}
                      alt={file.name}
                      className="w-full h-28 object-cover"
                      onLoad={() => URL.revokeObjectURL(previewUrl)}
                    />
                    <button
                      type="button"
                      onClick={() => removeSelectedFile(idx)}
                      disabled={isUploading}
                      className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded"
                      title="Remove"
                    >
                      ✕
                    </button>
                    <div className="p-2">
                      <p className="text-xs text-gray-700 truncate">
                        {file.name}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={isUploading || selectedFiles.length === 0}
          className={`w-full md:w-56 py-2 rounded-md transition duration-200 ${
            isUploading || selectedFiles.length === 0
              ? "bg-gray-400 cursor-not-allowed text-white"
              : "bg-blue-600 text-white hover:bg-blue-700"
          }`}
        >
          {isUploading
            ? "Uploading..."
            : `Save ${panelType === "business" ? "Business" : "Client"} Banners`}
        </button>
      </div>

      {/* Table Section */}
      <div className="bg-white rounded-lg shadow-lg p-4 md:p-6 overflow-x-auto">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
          <h3 className="text-xl font-semibold text-gray-700">
            Uploaded {panelType === "business" ? "Business" : "Client"} Banners
          </h3>
          <button
            onClick={() => fetchBanners(panelType)}
            className="text-sm px-3 py-1 border rounded hover:bg-gray-50"
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>

        {loading ? (
          <p className="text-gray-600">Loading banners...</p>
        ) : sortedBanners.length === 0 ? (
          <p className="text-gray-600">No {panelType} banners uploaded yet.</p>
        ) : (
          <table className="min-w-full bg-white border rounded-lg">
            <thead>
              <tr className="bg-gray-200">
                <th className="py-3 px-4 text-left font-semibold text-gray-600 border-b">
                  Index
                </th>
                <th className="py-3 px-4 text-left font-semibold text-gray-600 border-b">
                  Image
                </th>
                <th className="py-3 px-4 text-left font-semibold text-gray-600 border-b">
                  Panel
                </th>
                <th className="py-3 px-4 text-left font-semibold text-gray-600 border-b">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {sortedBanners.map((banner) => (
                <tr
                  key={banner._id}
                  className="hover:bg-gray-50 transition duration-150"
                >
                  <td className="py-3 px-4 border-b text-gray-700">
                    {banner?.index ?? "-"}
                  </td>

                  <td className="py-3 px-4 border-b">
                    <img
                      src={banner.url}
                      alt={`Banner ${banner?.index ?? ""}`}
                      className="w-20 h-20 object-cover rounded-md border"
                    />
                  </td>

                  <td className="py-3 px-4 border-b text-gray-700 capitalize">
                    {banner?.panelType || panelType}
                  </td>

                  <td className="py-3 px-4 border-b">
                    <button
                      className={`px-4 py-1 rounded text-white ${
                        deletingId === banner._id
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-red-500 hover:bg-red-600"
                      }`}
                      onClick={() => handleDelete(banner._id)}
                      disabled={deletingId === banner._id}
                    >
                      {deletingId === banner._id ? "Deleting..." : "Delete"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AddBanner;
